import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Help as HelpIcon,
  Psychology as KnowledgeIcon,
  Link as LinkIcon,
  Schedule as PendingIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  PlayArrow as StartIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Slider,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppDialog from '../components/AppDialog';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { verifyFactsAPI } from '../services/api';
import {
  getFactCheckItem,
  updateFactCheckItem,
  updateItemFacts,
} from '../services/factCheckItemService';
import { fetchRefinedTranscript, getValidPdfUrl, getValidVideoUrl } from '../services/gcs';
import { IMG } from '../theme/assets';
import type { ExtractedFact, FactCheckItemType, RefinedSegment } from '../types';
import { getQuote } from '../utils/quote';

// --- 個別のファクトアイテムコンポーネント（メモ化） ---
interface FactItemCardProps {
  fact: ExtractedFact;
  index: number;
  isSelected: boolean;
  isVerifying: boolean;
  itemType: FactCheckItemType | null;
  dark: boolean;
  cardBg: string;
  cardHoverBg: string;
  accentColor: string;
  borderColor: string;
  textPrimary: string;
  textSecondary: string;
  evidenceText: string;
  onToggle: (index: number) => void;
  onEdit: (index: number, field: keyof ExtractedFact, value: any) => void;
  onSeek: (seconds: number) => void;
  formatTimecode: (seconds: number) => string;
}

const FactItemCard = React.memo(
  ({
    fact,
    index,
    isSelected,
    isVerifying,
    itemType,
    dark,
    cardBg,
    cardHoverBg,
    accentColor,
    borderColor,
    textPrimary,
    textSecondary,
    evidenceText,
    onToggle,
    onEdit,
    onSeek,
    formatTimecode,
  }: FactItemCardProps) => {
    // 調べ方トグル（mock .seg）。値・onEdit は従来どおり。
    const strategies: {
      value: 'internal_knowledge' | 'google_search';
      label: string;
      icon: React.ReactNode;
    }[] = [
      {
        value: 'internal_knowledge',
        label: 'AIの知識',
        icon: <KnowledgeIcon sx={{ fontSize: 14 }} />,
      },
      { value: 'google_search', label: 'ネット検索', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
    ];
    const segBg = dark ? 'rgba(255,255,255,0.08)' : '#eef0f4';

    return (
      <Box
        sx={{
          mb: 1.5,
          p: '15px 18px',
          borderRadius: '14px',
          background: isSelected
            ? dark
              ? 'rgba(45,212,191,0.08)'
              : 'linear-gradient(180deg,#f1fbf8,#fff)'
            : cardBg,
          border: isSelected
            ? `1.5px solid ${dark ? accentColor : 'rgba(13,148,136,.3)'}`
            : `1px solid ${borderColor}`,
          boxShadow: isSelected ? 'none' : '0 5px 16px rgba(30,30,70,.05)',
          transition: 'border 0.18s ease, background 0.18s ease',
          '&:hover': { background: isSelected ? undefined : cardHoverBg },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* 検証対象の選択チェックボックス */}
          <Checkbox
            checked={isSelected}
            onChange={() => onToggle(index)}
            disabled={isVerifying}
            size="small"
            inputProps={{ 'aria-label': '検証対象に含める' }}
            sx={{
              p: 0,
              mt: '1px',
              flexShrink: 0,
              color: textSecondary,
              '&.Mui-checked': { color: accentColor },
              '& .MuiSvgIcon-root': { fontSize: 22 },
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* 主張（編集可） */}
            <TextField
              fullWidth
              multiline
              variant="standard"
              value={fact.claim}
              onChange={(e) => onEdit(index, 'claim', e.target.value)}
              disabled={isVerifying}
              slotProps={{ input: { disableUnderline: true } }}
              sx={{
                '& .MuiInputBase-root': { p: 0 },
                '& .MuiInputBase-input': {
                  color: textPrimary,
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.5,
                },
              }}
            />

            {/* 引用ブロック */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {(itemType === 'video_upload' || itemType === 'video_url') &&
                fact.start_sec !== undefined && (
                  <Chip
                    icon={<TimeIcon />}
                    label={formatTimecode(fact.start_sec)}
                    onClick={() => onSeek(fact.start_sec!)}
                    sx={{ cursor: 'pointer', height: 22, fontSize: 11 }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
            </Box>
            <Box
              sx={{
                mt: 0.75,
                pl: 1.75,
                position: 'relative',
                fontSize: 12.5,
                lineHeight: 1.55,
                color: evidenceText,
                '&::before': {
                  content: '"\\201C"',
                  position: 'absolute',
                  left: 0,
                  top: 1,
                  fontSize: 21,
                  fontWeight: 900,
                  lineHeight: 1,
                  color: '#9fcfc6',
                },
              }}
            >
              {getQuote(fact)}
            </Box>

            {/* 調べ方（mock .foot + .seg） */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5 }}>
              <Typography
                component="span"
                sx={{ fontSize: 11, fontWeight: 800, color: textSecondary }}
              >
                調べ方
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  background: segBg,
                  borderRadius: '9px',
                  p: '3px',
                }}
              >
                {strategies.map((opt) => {
                  const on = fact.verification_strategy === opt.value;
                  return (
                    <Box
                      component="button"
                      type="button"
                      key={opt.value}
                      onClick={() => onEdit(index, 'verification_strategy', opt.value)}
                      disabled={isVerifying}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontFamily: 'inherit',
                        fontSize: 11.5,
                        fontWeight: 800,
                        border: 'none',
                        borderRadius: '7px',
                        px: 1.25,
                        py: 0.7,
                        cursor: isVerifying ? 'default' : 'pointer',
                        color: on ? accentColor : textSecondary,
                        background: on ? (dark ? 'rgba(255,255,255,0.14)' : '#fff') : 'transparent',
                        boxShadow: on ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                        transition: '.12s',
                      }}
                    >
                      {opt.icon}
                      {opt.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

const FactCheckWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // テーマカラー変数
  const panelBg = themeMode === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.85)';
  const borderColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const textPrimary = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textSecondary = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  const textMuted = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)';
  const cardBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
  const cardHoverBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const evidenceText = themeMode === 'dark' ? '#cbd5e1' : '#475569';
  const overlayBg = themeMode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.9)';
  const speedControlBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';

  // 案件からの遷移の場合、戻り先を案件に設定
  const fromWorkspace = (location.state as any)?.fromWorkspace;
  const backPath = fromWorkspace ? `/share/${fromWorkspace}` : '/items';
  const backLabel = fromWorkspace ? '案件' : 'マイアイテム';

  // State管理
  const [facts, setFacts] = useState<ExtractedFact[]>([]);
  const [selectedFacts, setSelectedFacts] = useState<Set<number>>(new Set());
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // ローディング状態

  const [error, setError] = useState<string | null>(null);

  // アイテムタイプ（document or video） - 初期値nullで未確定を表す
  const [itemType, setItemType] = useState<FactCheckItemType | null>(null);

  // PDF/動画情報
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [filename, setFilename] = useState<string>('');

  // 動画プレイヤー用
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);

  // プロジェクト情報
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [projectDescription, setProjectDescription] = useState<string>('');

  // ダイアログ状態
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [isAddFactDialogOpen, setIsAddFactDialogOpen] = useState(false);

  // AIに調べて欲しいことを手動で追加する用
  const [newFactClaim, setNewFactClaim] = useState('');
  const [newFactEvidence, setNewFactEvidence] = useState('');
  const [newFactStrategy, setNewFactStrategy] = useState<'internal_knowledge' | 'google_search'>(
    'internal_knowledge',
  );

  // テキスト選択モード
  const [isTextSelectionMode, setIsTextSelectionMode] = useState(false);

  // 情報源表示モード用の新しい状態
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [originalStrategies, setOriginalStrategies] = useState<
    ExtractedFact['verification_strategy'][]
  >([]);

  // タブ切り替え用（動画の場合のみ使用）
  const [activeTab, setActiveTab] = useState<'facts' | 'transcript'>('facts');
  const [refinedTranscript, setRefinedTranscript] = useState<RefinedSegment[]>([]);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);

  // タブ切り替えハンドラー（遅延をUXで補う）
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'facts' | 'transcript') => {
    if (newValue === activeTab) return;
    setIsTabSwitching(true);
    // 次のフレームでタブを切り替え（ローディング表示を先に出す）
    requestAnimationFrame(() => {
      setActiveTab(newValue);
      // 切り替え完了後にローディングを解除
      requestAnimationFrame(() => {
        setIsTabSwitching(false);
      });
    });
  };

  useEffect(() => {
    const loadItem = async () => {
      if (!sessionId || !user) return;

      setIsLoading(true); // ローディング開始

      try {
        // 1. まず既存アイテムがあるかチェック（factcheck_itemsコレクション）
        const savedItem = await getFactCheckItem(sessionId);

        if (savedItem) {
          // 既存アイテムを復元
          setFacts(savedItem.facts || []);

          // デバッグログ
          console.log('🔍 savedItem:', savedItem);
          console.log('🔍 savedItem.type:', savedItem.type);
          console.log('🔍 savedItem.fileUrl:', savedItem.fileUrl);
          console.log('🔍 savedItem.sourceUrl:', savedItem.sourceUrl);

          // アイテムタイプを設定
          setItemType(savedItem.type || 'document');

          // 動画の場合
          if (savedItem.type === 'video_upload' || savedItem.type === 'video_url') {
            const currentVideoUrl =
              savedItem.type === 'video_upload' ? savedItem.fileUrl : savedItem.sourceUrl;
            console.log('🎬 currentVideoUrl:', currentVideoUrl);

            // video_uploadの場合、署名付きURLの更新が必要な可能性がある
            if (savedItem.type === 'video_upload' && savedItem.blobName) {
              try {
                const validVideoUrl = await getValidVideoUrl(
                  sessionId,
                  currentVideoUrl || '',
                  savedItem.blobName,
                );
                console.log('🎬 validVideoUrl:', validVideoUrl);
                setVideoUrl(validVideoUrl);

                // URLが更新された場合、Firestoreにも保存
                if (validVideoUrl !== currentVideoUrl) {
                  console.log('🔄 動画URLが更新されたため、Firestoreに保存します');
                  try {
                    await updateFactCheckItem(sessionId, { fileUrl: validVideoUrl });
                    console.log('✅ Firestoreに新しい動画URLを保存しました');
                  } catch (firestoreError) {
                    console.error('❌ Firestore更新エラー:', firestoreError);
                  }
                }
              } catch (error) {
                console.error('動画URL更新エラー:', error);
                setVideoUrl(currentVideoUrl || '');
              }
            } else {
              // video_urlの場合はそのまま使用
              setVideoUrl(currentVideoUrl || '');
            }
          } else {
            // ドキュメントの場合: PDF URLの期限切れチェック & 自動更新
            try {
              const validPdfUrl = await getValidPdfUrl(sessionId, savedItem.fileUrl || '');

              // URLが更新された場合、Firestoreにも保存
              if (validPdfUrl !== savedItem.fileUrl) {
                console.log(
                  '🔄 PDF URLが更新されたため、Firestoreに保存します (FactCheckWorkflow)',
                );
                console.log('📝 Item ID:', sessionId);

                try {
                  await updateFactCheckItem(sessionId, { fileUrl: validPdfUrl });
                  console.log('✅ Firestoreに新しいPDF URLを保存しました');
                } catch (firestoreError) {
                  console.error('❌ Firestore更新エラー:', firestoreError);
                }
              }

              setPdfUrl(validPdfUrl);
            } catch (error) {
              console.error('PDF URL更新エラー:', error);
              setPdfUrl(savedItem.fileUrl || ''); // フォールバック
            }
          }

          setFilename(savedItem.fileName || '');
          setProjectTitle(savedItem.title || '');
          setProjectDescription(savedItem.description || '');

          // 案件IDを設定（チャットボタン表示用）

          // 保存された選択状態を復元
          if (savedItem.selectedFactIndices && savedItem.selectedFactIndices.length > 0) {
            // 保存された選択状態を復元
            setSelectedFacts(new Set(savedItem.selectedFactIndices));
          } else {
            // 未設定または空配列の場合は全選択をデフォルトにする
            const allIndices = savedItem.facts?.map((_, index: number) => index) || [];
            setSelectedFacts(new Set(allIndices));
          }
        }
        // 2. 既存アイテムがない場合、location.stateから取得（CreateItemから遷移時）
        else if (location.state) {
          const {
            extractedFacts,
            pdfUrl: url,
            videoUrl: vidUrl,
            filename: name,
            itemTitle: title,
            itemDescription: description,
            itemType: type,
          } = location.state as any;

          setFacts(extractedFacts || []);
          setItemType(type || 'document');

          if (type === 'video_upload' || type === 'video_url') {
            setVideoUrl(vidUrl || '');
          } else {
            // 直接遷移時でも署名付きURLの検証を実施
            if (url && sessionId) {
              try {
                const validPdfUrl = await getValidPdfUrl(sessionId, url);
                setPdfUrl(validPdfUrl);
              } catch (error) {
                console.error('PDF URL検証エラー:', error);
                setPdfUrl(url);
              }
            } else {
              setPdfUrl(url || '');
            }
          }

          setFilename(name || '');
          setProjectTitle(title || name?.replace('.pdf', '') || '');
          setProjectDescription(description || '');

          // 案件IDを設定（遷移元から取得）
          if (fromWorkspace) {
          }

          // 初期状態では全ファクトを選択
          setSelectedFacts(new Set(extractedFacts?.map((_: any, index: number) => index) || []));

          // アイテムは既にCreateItemで作成済みなので、ファクトのみ更新
          if (extractedFacts && user.uid) {
            await updateItemFacts(sessionId, extractedFacts);
          }
        } else {
          setError('アイテムが見つかりません。');
        }
      } catch (error) {
        console.error('アイテム読み込みエラー:', error);
        setError('アイテムの読み込みに失敗しました。');
      } finally {
        setIsLoading(false); // ローディング終了
      }
    };

    loadItem();
  }, [location.state, sessionId, user, fromWorkspace]);

  // 動画の場合、補正済み文字起こしを取得
  useEffect(() => {
    const loadTranscript = async () => {
      if (!sessionId || (itemType !== 'video_upload' && itemType !== 'video_url')) {
        return;
      }

      setIsTranscriptLoading(true);
      try {
        const result = await fetchRefinedTranscript(sessionId);
        if (result?.segments) {
          setRefinedTranscript(result.segments);
          console.log('✅ 補正済み文字起こし読み込み完了:', result.segments.length, 'セグメント');
        }
      } catch (error) {
        console.error('補正済み文字起こし取得エラー:', error);
      } finally {
        setIsTranscriptLoading(false);
      }
    };

    loadTranscript();
  }, [sessionId, itemType]);

  // タイムコードフォーマット（秒 → MM:SS）
  const formatTimecode = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 動画シーク処理
  const handleSeekToTime = React.useCallback((seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
    }
  }, []);

  const handleFactToggle = React.useCallback((index: number) => {
    setSelectedFacts((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(index)) {
        newSelected.delete(index);
      } else {
        newSelected.add(index);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = () => {
    setSelectedFacts(new Set(facts.map((_, index) => index)));
  };

  const handleSelectNone = () => {
    setSelectedFacts(new Set());
  };

  // 情報源表示モードの切り替え
  const toggleSourceMode = () => {
    if (!isSourceMode) {
      // 情報源モード ON: 現在の戦略を保存し、全てGoogle検索に変更
      setOriginalStrategies(facts.map((f) => f.verification_strategy));
      const updatedFacts = facts.map((f) => ({
        ...f,
        verification_strategy: 'google_search' as const,
      }));
      setFacts(updatedFacts);
    } else {
      // 情報源モード OFF: 元の戦略を復元
      const restoredFacts = facts.map((f, i) => ({
        ...f,
        verification_strategy: originalStrategies[i] || 'internal_knowledge',
      }));
      setFacts(restoredFacts);
    }
    setIsSourceMode(!isSourceMode);
  };

  const handleFactEdit = React.useCallback(
    (index: number, field: keyof ExtractedFact, value: any) => {
      setFacts((prev) => {
        const updatedFacts = [...prev];
        updatedFacts[index] = { ...updatedFacts[index], [field]: value };
        return updatedFacts;
      });
    },
    [],
  );

  const handleStartVerification = async () => {
    if (selectedFacts.size === 0) {
      setError('検証するファクトを1つ以上選択してください。');
      return;
    }

    if (!sessionId) {
      setError('セッションIDが見つかりません。');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const selectedFactsArray = Array.from(selectedFacts).map((index) => facts[index]);
      const response = await verifyFactsAPI(sessionId, selectedFactsArray);

      if (response.success && response.data) {
        // 段階的更新APIなので、processing状態のレスポンスが返される
        console.log('✅ ファクトチェック開始成功:', response.data);

        // factcheck_itemsコレクションを更新（検証中状態に変更）
        if (user?.uid) {
          try {
            await updateFactCheckItem(sessionId, {
              status: 'verifying',
              facts: facts,
              selectedFactIndices: Array.from(selectedFacts),
              verification_results: [],
            });
          } catch (error) {
            console.error('Firestore更新エラー:', error);
          }
        }

        // アイテム情報を構築（ProcessingResultsに渡すため）
        const item = {
          id: sessionId,
          title: projectTitle || filename.replace('.pdf', ''),
          description: projectDescription || `${facts.length}件のファクトを含むファクトチェック`,
          status: 'verifying' as const,
          type: itemType, // 動画/ドキュメントの判定用
          fileName: filename,
          fileUrl: itemType === 'video_upload' || itemType === 'video_url' ? videoUrl : pdfUrl,
          total_facts: facts.length,
          verified_facts: 0,
          facts: facts,
          verification_results: [],
          selectedFactIndices: Array.from(selectedFacts),
        };

        // ProcessingResultsページに遷移（段階的更新画面）
        navigate(`/processing/${sessionId}`, {
          state: {
            item,
            selectedFactsCount: selectedFacts.size,
          },
        });
      } else {
        setError(response.error || '検証に失敗しました。');
      }
    } catch (_err) {
      setError('検証中にエラーが発生しました。');
    } finally {
      setIsVerifying(false);
    }
  };

  // テキスト選択モードを開始
  const handleSelectFromPdf = () => {
    setIsTextSelectionMode(true);
    setIsAddFactDialogOpen(false); // モーダルを閉じる
  };

  // テキスト選択を確定
  const handleConfirmTextSelection = () => {
    // ボタンクリック時のテキスト選択を避けるため少し待機
    setTimeout(() => {
      // iframe内の選択は検出できないため、クリップボードからテキストを取得
      navigator.clipboard
        .readText()
        .then((text) => {
          const trimmedText = text.trim();
          // 除外すべきテキストのリスト
          const excludedTexts = ['この範囲をEvidenceに設定', '確定', 'キャンセル', ''];

          if (trimmedText && !excludedTexts.includes(trimmedText)) {
            setNewFactEvidence(trimmedText);
          } else {
            // 何もコピーされていない場合は空のまま
            setNewFactEvidence('');
          }
          setIsTextSelectionMode(false);
          setIsAddFactDialogOpen(true); // モーダルを再度開く
        })
        .catch((err) => {
          console.error('クリップボード読み取りエラー:', err);
          // フォールバック: 手動入力を促す
          const manualText = prompt('PDFから選択したテキストを貼り付けてください:');
          if (manualText?.trim()) {
            const trimmedManualText = manualText.trim();
            const excludedTexts = ['この範囲をEvidenceに設定', '確定', 'キャンセル'];

            if (!excludedTexts.includes(trimmedManualText)) {
              setNewFactEvidence(trimmedManualText);
              setIsTextSelectionMode(false);
              setIsAddFactDialogOpen(true);
            }
          }
        });
    }, 100); // 100ms待機してボタンのテキスト選択を回避
  };

  // テキスト選択をキャンセル
  const handleCancelTextSelection = () => {
    setIsTextSelectionMode(false);
    setIsAddFactDialogOpen(true); // モーダルを再度開く
  };

  // 現在の状態を保存
  const handleSaveCurrentState = async () => {
    if (!sessionId || !user?.uid) {
      setError('セッションIDまたはユーザー情報が見つかりません。');
      return;
    }

    try {
      // factcheck_itemsコレクションを部分更新
      await updateFactCheckItem(sessionId, {
        title: projectTitle || filename.replace('.pdf', ''),
        description:
          projectDescription || `${facts.length}件のファクトを含むファクトチェック（編集中）`,
        facts: facts,
        selectedFactIndices: Array.from(selectedFacts),
        total_facts: facts.length,
      });

      // 成功メッセージを表示
      setError(null);
      alert('現在の状態を保存しました。');
    } catch (err) {
      setError('保存中にエラーが発生しました。');
      console.error('保存エラー:', err);
    }
  };

  // PDF内でのテキスト選択を監視
  useEffect(() => {
    if (!isTextSelectionMode) return;

    const handleSelectionChange = () => {
      // 選択処理はクリップボードベースなので、この処理は不要
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isTextSelectionMode]);

  return (
    <Box
      sx={{
        height: 'calc(var(--app-vh) - 128px)', // Layout: mt:8(64px) + p:4上下(32px*2) = 128px
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minHeight: 0, // flexbox の子要素が正しく伸縮するために必要
        p: 0, // Remove global padding as header goes full width
      }}
    >
      {/* テキスト選択モード時のガイドとボタン */}
      {isTextSelectionMode && (
        <>
          {/* ガイドメッセージ */}
          <Box
            sx={{
              position: 'fixed',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: 2,
              zIndex: 2000,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            PDFからテキストを選択してコピー（Ctrl+C / Cmd+C）後、「確定」ボタンを押してください
          </Box>
        </>
      )}
      {/* ヘッダー（mock .rhead: 帯なし） */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          px: 1.5,
          py: 1.5,
          zIndex: 1,
          ...(isTextSelectionMode && {
            filter: 'blur(4px)',
            opacity: 0.3,
            pointerEvents: 'none',
          }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{
              background: themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)',
              border: `1px solid ${tokens.line}`,
              borderRadius: '11px',
              px: 1.75,
              py: 0.9,
              fontSize: 13,
              fontWeight: 700,
              color: textPrimary,
              textTransform: 'none',
              flexShrink: 0,
              '&:hover': {
                background: themeMode === 'dark' ? 'rgba(255,255,255,0.14)' : '#fff',
              },
            }}
          >
            {backLabel}
          </Button>
          <Typography
            sx={{
              fontSize: 21,
              fontWeight: 900,
              color: textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            抽出ファクトのレビュー:{' '}
            <Box component="span" sx={{ color: tokens.teal }}>
              {projectTitle}
            </Box>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          <Button
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveCurrentState}
            disabled={isVerifying}
            sx={{
              background: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)',
              border: `1px solid ${tokens.line}`,
              borderRadius: '11px',
              px: 1.5,
              py: 0.8,
              fontSize: 12.5,
              fontWeight: 700,
              color: textPrimary,
              textTransform: 'none',
              '&:hover': { background: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : '#fff' },
            }}
          >
            保存
          </Button>
          <Button
            size="small"
            startIcon={isVerifying ? <PendingIcon /> : <StartIcon />}
            onClick={handleStartVerification}
            disabled={isVerifying || selectedFacts.size === 0}
            sx={{
              background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
              color: '#fff',
              fontWeight: 800,
              fontSize: 13,
              borderRadius: '11px',
              px: 2,
              py: 0.9,
              textTransform: 'none',
              boxShadow: '0 8px 20px rgba(40,50,90,0.2)',
              '&:hover': { filter: 'brightness(0.96)' },
              '&.Mui-disabled': { background: tokens.line, color: tokens.faint, boxShadow: 'none' },
            }}
          >
            {isVerifying ? '検証中...' : `ファクトチェック実行（${selectedFacts.size}件）`}
          </Button>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 進行状況 */}
      {isVerifying && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            ファクトチェックを実行中... しばらくお待ちください
          </Typography>
        </Box>
      )}

      {/* メインコンテンツ: 左右分割 */}
      <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, minHeight: 0, overflow: 'hidden', p: 2 }}>
        {/* 左パネル: AIが見つけたチェック項目 */}
        <Box
          sx={{
            width: '40%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0, // flexbox用
            ...(isTextSelectionMode && {
              filter: 'blur(4px)',
              opacity: 0.3,
              pointerEvents: 'none',
            }),
          }}
        >
          {/* レビュー導入バナー（mock .rev）— 文書/動画 共通・最上部 */}
          {itemType !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: '12px 16px',
                borderRadius: '12px',
                flexShrink: 0,
                border: `1px solid ${borderColor}`,
                background:
                  themeMode === 'dark'
                    ? `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`
                    : 'linear-gradient(120deg,#eef5ff,#eafaf6)',
                boxShadow: '0 8px 24px rgba(30,30,70,.06)',
              }}
            >
              <Box
                component="img"
                src={IMG.owl.clipboard}
                alt=""
                sx={{
                  width: 52,
                  height: 52,
                  objectFit: 'contain',
                  flexShrink: 0,
                  filter: 'drop-shadow(0 6px 10px rgba(30,30,70,.14))',
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: textPrimary }}>
                  検証する主張を選びましょう。
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: textSecondary, mt: 0.25, lineHeight: 1.45 }}
                >
                  主張を確認・編集し、調べ方を選んで開始します。
                </Typography>
              </Box>
            </Box>
          )}

          {/* 操作ヘッダー（カードではなく軽量ツールバー） */}
          <Box sx={{ flexShrink: 0, mb: 2 }}>
            {/* アイテムタイプ未確定の場合: ローディング表示 */}
            {itemType === null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pl: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: textMuted }}>
                  読み込み中...
                </Typography>
              </Box>
            ) : itemType === 'video_upload' || itemType === 'video_url' ? (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                      minHeight: 'auto',
                      '& .MuiTab-root': {
                        color: textSecondary,
                        minHeight: 'auto',
                        '&.Mui-selected': { color: accentColor },
                      },
                      '& .MuiTabs-indicator': { backgroundColor: accentColor },
                    }}
                  >
                    <Tab
                      value="facts"
                      label={`AIが見つけたチェック項目（${selectedFacts.size}件）`}
                    />
                    <Tab
                      value="transcript"
                      label={`文字起こし${refinedTranscript.length > 0 ? `（${refinedTranscript.length}件）` : ''}`}
                      disabled={isTranscriptLoading}
                    />
                  </Tabs>

                  {/* AIが見つけたチェック項目タブの操作ボタン（タブ脇に配置） */}
                  {activeTab === 'facts' && (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                      {/* 全選択/全解除アイコンボタン */}
                      <Tooltip title="全選択" arrow>
                        <IconButton
                          onClick={handleSelectAll}
                          size="small"
                          sx={{
                            color: 'primary.main',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            p: 0.5,
                          }}
                        >
                          <CheckBoxIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="全解除" arrow>
                        <IconButton
                          onClick={handleSelectNone}
                          size="small"
                          sx={{
                            color: 'grey.500',
                            border: '1px solid',
                            borderColor: 'grey.500',
                            borderRadius: 1,
                            p: 0.5,
                          }}
                        >
                          <CheckBoxOutlineBlankIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* 区切り */}
                      <Box sx={{ width: 8 }} />

                      {/* URL付き検証モード */}
                      <Chip
                        icon={<LinkIcon />}
                        label={
                          isSourceMode
                            ? '全てにインターネットのリンク先を付ける ON'
                            : '全てにインターネットのリンク先を付ける'
                        }
                        onClick={toggleSourceMode}
                        variant={isSourceMode ? 'filled' : 'outlined'}
                        color={isSourceMode ? 'info' : 'default'}
                        size="small"
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          ...(isSourceMode && {
                            backgroundColor: 'info.main',
                            color: 'info.contrastText',
                          }),
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              /* ドキュメントの場合: ヘッダー */
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  pl: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 900,
                    minWidth: 'fit-content',
                    color: textPrimary,
                  }}
                >
                  AIが見つけたチェック項目（
                  <Box component="span" sx={{ color: tokens.teal }}>
                    {selectedFacts.size}
                  </Box>
                  件選択中）
                </Typography>

                {/* 右側のボタングループ */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {/* 全選択/全解除アイコンボタン */}
                  <Tooltip title="全選択" arrow>
                    <IconButton
                      onClick={handleSelectAll}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    >
                      <CheckBoxIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="全解除" arrow>
                    <IconButton
                      onClick={handleSelectNone}
                      size="small"
                      sx={{
                        color: 'grey.500',
                        border: '1px solid',
                        borderColor: 'grey.500',
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    >
                      <CheckBoxOutlineBlankIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* 区切り */}
                  <Box sx={{ width: 8 }} />

                  {/* URL付き検証モード */}
                  <Chip
                    icon={<LinkIcon />}
                    label={
                      isSourceMode
                        ? '全てにインターネットのリンク先を付ける ON'
                        : '全てにインターネットのリンク先を付ける'
                    }
                    onClick={toggleSourceMode}
                    variant={isSourceMode ? 'filled' : 'outlined'}
                    color={isSourceMode ? 'info' : 'default'}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      ...(isSourceMode && {
                        backgroundColor: 'info.main',
                        color: 'info.contrastText',
                      }),
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* ドキュメントの場合のみ表示（動画では非表示） */}
            {itemType === 'document' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<HelpIcon />}
                  onClick={() => setIsHelpDialogOpen(true)}
                  sx={{ flex: 1 }}
                >
                  読み込ませた文書の中の言葉を探す方法
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddFactDialogOpen(true)}
                  disabled={isVerifying}
                  sx={{ flex: 1 }}
                >
                  AIに調べて欲しいことを手動で追加
                </Button>
              </Box>
            )}
          </Box>

          {/* ファクト/文字起こしリスト（このエリアのみスクロール） */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              pr: 0.5,
              pb: 1,
              position: 'relative',
              minHeight: 0,
            }}
          >
            {/* タブ切り替え中のローディングオーバーレイ */}
            {isTabSwitching && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: overlayBg,
                  zIndex: 10,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            {/* AIが見つけたチェック項目タブ（ドキュメント、または動画でファクトタブ選択時） */}
            {/* display:noneで切り替え（DOMを保持してタブ切り替えを高速化） */}
            <Box
              sx={{
                display: itemType === 'document' || activeTab === 'facts' ? 'block' : 'none',
              }}
            >
              {/* ローディング中 */}
              {isLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2, color: textSecondary }}>読み込み中...</Typography>
                </Box>
              )}

              {/* ファクト0件の場合（ローディング完了後のみ表示） */}
              {!isLoading && facts.length === 0 && (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'warning.dark',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <HelpIcon sx={{ fontSize: 48, color: 'warning.light' }} />
                  </Box>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: textPrimary }}>
                    検証可能なファクトが見つかりませんでした
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 3, color: textSecondary }}>
                    この動画には、ファクトチェック対象となる
                    <br />
                    客観的に検証可能な主張が含まれていませんでした。
                  </Typography>

                  <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      ファクトチェック対象となる内容の例：
                    </Typography>
                    <Typography variant="body2" component="div">
                      ✅ 統計データ・数値情報
                      <br />✅ 歴史的事実
                      <br />✅ 公式発表・イベント情報
                      <br />✅ 人物の基本プロフィール
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                      対象外となる内容の例：
                    </Typography>
                    <Typography variant="body2" component="div">
                      ❌ 個人的な意見・感想
                      <br />❌ 主観的な評価
                      <br />❌ 未来の予測
                      <br />❌ 相談・アドバイス内容
                    </Typography>
                  </Alert>

                  {/* ドキュメントの場合のみ表示 */}
                  {itemType === 'document' && (
                    <Typography variant="body2" sx={{ color: textMuted }}>
                      「AIに調べて欲しいことを手動で追加」ボタンから、自分で調べて欲しいことを足すこともできます。
                    </Typography>
                  )}
                </Box>
              )}

              {/* AIが見つけたチェック項目 */}
              {facts.map((fact, index) => (
                <FactItemCard
                  key={index}
                  fact={fact}
                  index={index}
                  isSelected={selectedFacts.has(index)}
                  isVerifying={isVerifying}
                  itemType={itemType}
                  dark={themeMode === 'dark'}
                  cardBg={cardBg}
                  cardHoverBg={cardHoverBg}
                  accentColor={accentColor}
                  borderColor={borderColor}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  evidenceText={evidenceText}
                  onToggle={handleFactToggle}
                  onEdit={handleFactEdit}
                  onSeek={handleSeekToTime}
                  formatTimecode={formatTimecode}
                />
              ))}
            </Box>

            {/* 文字起こしタブ（動画でトランスクリプトタブ選択時） */}
            {/* display:noneで切り替え（DOMを保持してタブ切り替えを高速化） */}
            <Box
              sx={{
                display:
                  (itemType === 'video_upload' || itemType === 'video_url') &&
                  activeTab === 'transcript'
                    ? 'block'
                    : 'none',
              }}
            >
              {/* 文字起こしローディング中 */}
              {isTranscriptLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2, color: textSecondary }}>
                    文字起こしを読み込み中...
                  </Typography>
                </Box>
              )}

              {/* 文字起こしがない場合 */}
              {!isTranscriptLoading && refinedTranscript.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                  <Typography variant="body1" sx={{ color: textSecondary }}>
                    補正済み文字起こしがまだ生成されていません。
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: textMuted }}>
                    ファクト抽出処理と同時に生成されます。
                  </Typography>
                </Box>
              )}

              {/* 文字起こし一覧 */}
              {!isTranscriptLoading && refinedTranscript.length > 0 && (
                <Box>
                  {refinedTranscript.map((segment) => (
                    <Box
                      key={segment.segment_id}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        backgroundColor: cardBg,
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        '&:hover': {
                          backgroundColor: cardHoverBg,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {/* タイムコード（クリックでシーク） */}
                        <Chip
                          icon={<TimeIcon />}
                          label={formatTimecode(segment.start_sec)}
                          onClick={() => handleSeekToTime(segment.start_sec)}
                          sx={{
                            cursor: 'pointer',
                            minWidth: 90,
                            '& .MuiChip-label': { fontFamily: 'monospace' },
                          }}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                        {/* 補正後テキスト */}
                        <Typography
                          variant="body2"
                          sx={{
                            flexGrow: 1,
                            color: textPrimary,
                            lineHeight: 1.6,
                          }}
                        >
                          {segment.refined_text}
                        </Typography>
                      </Box>
                      {/* 原文との差分がある場合、原文も表示 */}
                      {segment.original_text !== segment.refined_text && (
                        <Box sx={{ mt: 1, pl: '106px' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: textMuted,
                              fontStyle: 'italic',
                              display: 'block',
                            }}
                          >
                            原文: {segment.original_text}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* 右パネル: PDF表示 */}
        <Box
          sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {/* テキスト選択モード時のボタンと注意書き（PDFプレビュー左上） */}
          {isTextSelectionMode && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1500,
                display: 'flex',
                gap: 3,
                alignItems: 'center',
              }}
            >
              {/* 注意書き */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                <Box sx={{ fontSize: '1.5rem' }}>⚠️</Box>
                <Typography
                  sx={{
                    color: 'error.main',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  選択後、Ctrl+C / Cmd+C でコピー
                </Typography>
              </Box>

              {/* ボタン */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirmTextSelection}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: 'success.main',
                    userSelect: 'none', // テキスト選択を無効化
                    '&:hover': {
                      backgroundColor: 'success.dark',
                    },
                  }}
                >
                  確定
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCancelTextSelection}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: 'error.main',
                    color: 'white',
                    userSelect: 'none', // テキスト選択を無効化
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    },
                  }}
                >
                  キャンセル
                </Button>
              </Box>
            </Box>
          )}

          <Card
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0, // flexbox用
              background: panelBg,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${borderColor}`,
              borderRadius: 3,
              boxShadow: 'none', // Remove shadow/lift
              transition: 'none',
              '&:hover': {
                background: panelBg,
                transform: 'none',
                boxShadow: 'none',
              },
              // テキスト選択モード時はPDFエリアのヘッダーのみブラー
              ...(isTextSelectionMode && {
                '& .pdf-header': {
                  filter: 'blur(4px)',
                  opacity: 0.3,
                  pointerEvents: 'none',
                },
              }),
            }}
          >
            <CardContent
              sx={{ pb: 1, flexShrink: 0 }}
              className={isTextSelectionMode ? 'pdf-header' : ''}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                {itemType !== null && (
                  <Box
                    component="img"
                    src={itemType === 'document' ? IMG.preview.document : IMG.preview.video}
                    alt=""
                    sx={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }}
                  />
                )}
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: textPrimary }}>
                  {itemType === null
                    ? '読み込み中...'
                    : itemType === 'document'
                      ? 'ドキュメントプレビュー'
                      : '動画プレビュー'}
                </Typography>
              </Box>
            </CardContent>

            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 0,
                backgroundColor: 'transparent',
                minHeight: 0,
              }}
            >
              {/* アイテムタイプ未確定の場合: ローディング表示 */}
              {itemType === null && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {/* ドキュメントの場合: PDF表示 */}
              {itemType === 'document' &&
                (pdfUrl ? (
                  <Box sx={{ height: '100%', p: 2, minHeight: 0 }}>
                    <Box
                      sx={{
                        height: '100%',
                        border: `1px solid ${borderColor}`,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        background: '#fff',
                      }}
                    >
                      <iframe
                        src={pdfUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="PDF Preview"
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      m: 2,
                      p: 3,
                      borderRadius: '12px',
                      border: `1px solid ${borderColor}`,
                      background: themeMode === 'dark' ? 'rgba(0,0,0,0.2)' : '#f6f9f8',
                      display: 'grid',
                      placeItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Box>
                      <Box
                        component="img"
                        src={IMG.preview.document}
                        alt=""
                        sx={{ width: 48, height: 48, objectFit: 'contain', opacity: 0.75, mb: 1.5 }}
                      />
                      <Typography
                        sx={{ color: textPrimary, fontWeight: 700, fontSize: 14, mb: 0.5 }}
                      >
                        {filename}
                      </Typography>
                      <Typography sx={{ color: textMuted, fontSize: 12.5 }}>
                        PDFを読み込み中...
                      </Typography>
                    </Box>
                  </Box>
                ))}

              {/* 動画の場合: 動画プレイヤー */}
              {(itemType === 'video_upload' || itemType === 'video_url') && (
                <Box
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  {/* 動画ステージ（結果/processingと同じ vstage 調） */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      minHeight: 0,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #1b2030, #39405a)',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'rgba(255,255,255,0.55)',
                    }}
                  >
                    {videoUrl ? (
                      <video
                        ref={videoRef}
                        src={videoUrl}
                        controls
                        style={{
                          width: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Box sx={{ textAlign: 'center', px: 2 }}>
                        <Box
                          component="svg"
                          viewBox="0 0 24 24"
                          sx={{ width: 56, height: 56, fill: 'currentColor' }}
                        >
                          <path d="M8 5v14l11-7z" />
                        </Box>
                        <Typography sx={{ mt: 1, fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
                          {filename}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* 再生速度コントロール（動画読み込み時のみ） */}
                  {videoUrl && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mt: 2,
                        p: 1.5,
                        backgroundColor: speedControlBg,
                        borderRadius: 2,
                      }}
                    >
                      <SpeedIcon sx={{ color: textSecondary }} />
                      <Typography sx={{ color: textSecondary, minWidth: 100 }}>
                        再生速度: {playbackRate}x
                      </Typography>
                      <Slider
                        value={playbackRate}
                        min={0.5}
                        max={3.0}
                        step={0.25}
                        marks={[
                          { value: 0.5, label: '0.5x' },
                          { value: 1.0, label: '1x' },
                          { value: 2.0, label: '2x' },
                          { value: 3.0, label: '3x' },
                        ]}
                        onChange={(_, value) => {
                          setPlaybackRate(value as number);
                          if (videoRef.current) {
                            videoRef.current.playbackRate = value as number;
                          }
                        }}
                        sx={{
                          flex: 1,
                          color: 'primary.main',
                          '& .MuiSlider-markLabel': {
                            color: textMuted,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>

      {/* ヘルプダイアログ - 読み込ませた文書の中の言葉を探す方法 */}
      <AppDialog
        open={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
        icon={<HelpIcon />}
        title="読み込ませた文書の中の言葉を探す方法"
        subtitle="3つの手順で見つかります"
        size="md"
      >
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            手順
          </Typography>
          <Box sx={{ pl: 2, mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>1. 検索したいテキストをコピー</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              各ファクトの「引用」欄に表示されているテキストを手動でコピーしてください
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>2. 検索画面を出す</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              PDFプレビュー画面で <kbd>Cmd+F</kbd> (Mac) または <kbd>Ctrl+F</kbd> (Windows)
              で検索バーを開く
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>3. 検索窓にテキストをペースト</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
              コピーした引用テキストを検索バーに貼り付け（<kbd>Cmd+V</kbd> / <kbd>Ctrl+V</kbd>
              ）すると、PDF内でハイライト表示されます
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>ヒント:</strong>{' '}
              引用テキストの一部（特徴的な単語）だけでも検索できます。完全に一致しない場合は、テキストの一部分で検索してみてください。
            </Typography>
          </Alert>
        </Box>
      </AppDialog>

      {/* AIに調べて欲しいことを手動で追加するダイアログ */}
      <AppDialog
        open={isAddFactDialogOpen}
        onClose={() => setIsAddFactDialogOpen(false)}
        icon={<AddIcon />}
        title="AIに調べて欲しいことを追加"
        subtitle="検証したい主張を手で足します"
        size="md"
        actions={
          <>
            <Button
              onClick={() => {
                setIsAddFactDialogOpen(false);
                setNewFactClaim('');
                setNewFactEvidence('');
                setNewFactStrategy('internal_knowledge');
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: textSecondary,
                borderRadius: '10px',
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={() => {
                if (newFactClaim.trim() && newFactEvidence.trim()) {
                  const newFact: ExtractedFact = {
                    claim: newFactClaim.trim(),
                    quote: newFactEvidence.trim(),
                    verification_strategy: newFactStrategy,
                    page: 1,
                    line_start: facts.length + 1,
                    confidence: 0.8,
                  };

                  setFacts([...facts, newFact]);
                  setSelectedFacts(new Set([...selectedFacts, facts.length]));
                  setIsAddFactDialogOpen(false);
                  setNewFactClaim('');
                  setNewFactEvidence('');
                  setNewFactStrategy('internal_knowledge');
                }
              }}
              disabled={!newFactClaim.trim() || !newFactEvidence.trim()}
              sx={{
                background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                color: '#fff',
                fontWeight: 800,
                borderRadius: '12px',
                textTransform: 'none',
                px: 2.5,
                boxShadow: 'none',
                '&:hover': {
                  background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                  filter: 'brightness(0.96)',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  background: themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : '#cbd2e0',
                  color: '#fff',
                },
              }}
            >
              追加する
            </Button>
          </>
        }
      >
        <Box sx={{ py: 2 }}>
          <TextField
            fullWidth
            label="主張 (Claim)"
            multiline
            rows={3}
            value={newFactClaim}
            onChange={(e) => setNewFactClaim(e.target.value)}
            placeholder="検証したい主張を入力してください..."
            sx={{ mb: 3 }}
            required
          />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="引用"
                multiline
                rows={3}
                value={newFactEvidence}
                placeholder="右のボタンから指定してください。"
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                required
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f4f6f8',
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: textPrimary,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSelectFromPdf}
                sx={{
                  minWidth: 120,
                  height: 56,
                  mt: 0.5,
                  color: '#fff',
                  background: tokens.teal,
                  fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { background: tokens.teal, filter: 'brightness(0.95)' },
                }}
              >
                PDFから選択
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>使い方:</strong> PDFからテキストを選択してコピー（Ctrl+C /
                Cmd+C）してから「PDFから選択」ボタンを押してください。
              </Typography>
            </Alert>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: textSecondary, mb: 1 }}>
              検証方法
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              {[
                {
                  value: 'internal_knowledge' as const,
                  label: 'AIの知識',
                  desc: '内部の知識で検証',
                  icon: <KnowledgeIcon sx={{ fontSize: 18 }} />,
                },
                {
                  value: 'google_search' as const,
                  label: 'ネット検索',
                  desc: '出典付きで検証',
                  icon: <SearchIcon sx={{ fontSize: 18 }} />,
                },
              ].map((opt) => {
                const on = newFactStrategy === opt.value;
                return (
                  <Box
                    component="button"
                    type="button"
                    key={opt.value}
                    onClick={() => setNewFactStrategy(opt.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      border: `1.5px solid ${on ? tokens.teal : tokens.line}`,
                      borderRadius: '12px',
                      p: '10px 12px',
                      transition: '.12s',
                      background: on
                        ? themeMode === 'dark'
                          ? 'rgba(45,212,191,0.12)'
                          : '#eafaf6'
                        : themeMode === 'dark'
                          ? 'rgba(255,255,255,0.04)'
                          : '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '9px',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: on
                          ? tokens.teal
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : '#eef0f4',
                        color: on ? '#fff' : textSecondary,
                      }}
                    >
                      {opt.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: on ? tokens.teal : textPrimary,
                        }}
                      >
                        {opt.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: textSecondary }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              追加されたファクトは自動的に選択状態になり、ファクトチェック実行時に検証されます。
            </Typography>
          </Alert>
        </Box>
      </AppDialog>

      {/* 案件所属アイテムの場合のみフローティングチャットボタンを表示 */}
    </Box>
  );
};

export default FactCheckWorkflow;
